// src/components/DomainsViewTable.js
// Table-based layout for Impersonating Domains with DNS lookup (WhoisFreaks)
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTenant } from '../TenantProvider';
import { Globe, Search, Edit, Save, X, Download, ChevronDown, ChevronRight, ArrowUpDown, Plus, RefreshCw, Shield, Calendar, Server, PieChart, Camera, ExternalLink, Layers } from 'lucide-react';

// ─── Chart color maps (hex for SVG strokes) ───
const RISK_ORDER = ['Critical', 'High', 'Medium', 'Low', 'Informational'];
const RISK_HEX = { Critical: '#ef4444', High: '#f97316', Medium: '#eab308', Low: '#3b82f6', Informational: '#6b7280' };

const STATUS_ORDER = ['Active', 'Monitoring', 'Takedown Requested', 'Takedown Successful', 'Resolved', 'False Positive'];
const STATUS_HEX = {
  'Active': '#ef4444', 'Monitoring': '#06b6d4', 'Takedown Requested': '#a855f7',
  'Takedown Successful': '#8b5cf6', 'Resolved': '#22c55e', 'False Positive': '#6b7280'
};

const TYPE_HEX = {
  'Typosquatting': '#06b6d4', 'Phishing': '#ef4444', 'Homograph Attack': '#a855f7',
  'Combosquatting': '#f59e0b', 'Brand Jacking': '#ec4899', 'Look-alike Domain': '#3b82f6', 'Other': '#94a3b8'
};

const SCORE_BRACKETS = [
  { label: '90–100', filterVal: '90-100', hex: '#ef4444' },
  { label: '70–89', filterVal: '70-89', hex: '#f97316' },
  { label: '50–69', filterVal: '50-69', hex: '#eab308' },
  { label: 'Below 50', filterVal: 'below-50', hex: '#3b82f6' },
  { label: 'No score', filterVal: 'none', hex: '#6b7280' },
];

const EMPTY_BULK_EDITS = { type: '', status: '', risk: '', similarity: '' };
const BULK_FIELD_LABELS = { type: 'Type', status: 'Status', risk: 'Risk', similarity: 'Phishing Score' };

const BULK_LOOKUPS = {
  dns: { label: 'DNS lookup', path: 'dns-lookup' },
  whois: { label: 'WHOIS lookup', path: 'whois-lookup' }
};

// Accepts "95" or "95%", stores the bare number the existing rows use.
const normalizeScore = (raw) => {
  const str = String(raw).trim().replace('%', '').trim();
  if (str === '') return null;
  const num = Number(str);
  if (!Number.isFinite(num) || num < 0 || num > 100) return null;
  return String(num);
};

// Display bare, tolerating rows that were stored with a trailing "%".
const formatScore = (similarity) => {
  const str = String(similarity ?? '').trim().replace(/%\s*$/, '').trim();
  return str === '' ? null : str;
};

const DomainsViewTable = ({ canSeeSuperAdminSettings, canEditIncidents, setShowAddImpersonatingDomain }) => {
  const { getToken, isSignedIn } = useAuth();
  const { activeCompany } = useTenant();

  // State
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterScore, setFilterScore] = useState('all');
  const [filterRisk, setFilterRisk] = useState('all');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showCharts, setShowCharts] = useState(true);

  // Table state
  const [expandedRow, setExpandedRow] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedDomain, setEditedDomain] = useState(null);
  const [sortField, setSortField] = useState('domain_id');
  const [sortDir, setSortDir] = useState('desc');
  const [dnsLoading, setDnsLoading] = useState(null);
  const [whoisLoading, setWhoisLoading] = useState(null);
  const [urlscanLoading, setUrlscanLoading] = useState(null);
  const [showDnsExtra, setShowDnsExtra] = useState(false);

  // Bulk edit state
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkEdits, setBulkEdits] = useState(EMPTY_BULK_EDITS);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);
  const [bulkLookup, setBulkLookup] = useState(null); // 'dns' | 'whois' while a bulk enrichment runs

  const exportMenuRef = useRef(null);

  // ─── Fetch domains ───
  useEffect(() => {
    if (!isSignedIn || !activeCompany) return;
    const fetchDomains = async () => {
      setLoading(true);
      setError(null);
      setSelectedIds(new Set());
      try {
        const token = await getToken();
        const res = await fetch(`/api/companies/${activeCompany.id}/impersonating-domains`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error(`Failed to load domains (${res.status})`);
        const data = await res.json();
        setDomains(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Domains load error:', e);
        setError(e.message || 'Failed to load domains');
      } finally {
        setLoading(false);
      }
    };
    fetchDomains();
  }, [isSignedIn, activeCompany, getToken]);

  // ─── Close export menu on click outside ───
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setShowExportMenu(false);
      }
    };
    if (showExportMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  // ─── parseScore ───
  const parseScore = (similarity) => {
    if (!similarity) return null;
    const str = String(similarity).trim().replace('%', '');
    const num = parseFloat(str);
    if (isNaN(num)) return null;
    return num > 1 && num <= 100 ? num : num <= 1 ? num * 100 : num;
  };

  // ─── Filter ───
  const filteredDomains = domains.filter(domain => {
    const matchesSearch = domain.domain?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         domain.domain_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         domain.ip_address?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || domain.type === filterType;
    const matchesStatus = filterStatus === 'all' || domain.status === filterStatus;
    const matchesRisk = filterRisk === 'all' || domain.risk === filterRisk;
    let matchesScore = true;
    if (filterScore !== 'all') {
      const score = parseScore(domain.similarity);
      if (score === null) {
        matchesScore = filterScore === 'none';
      } else if (filterScore === '90-100') {
        matchesScore = score >= 90;
      } else if (filterScore === '70-89') {
        matchesScore = score >= 70 && score < 90;
      } else if (filterScore === '50-69') {
        matchesScore = score >= 50 && score < 70;
      } else if (filterScore === 'below-50') {
        matchesScore = score < 50;
      } else if (filterScore === 'none') {
        matchesScore = score === null;
      }
    }
    return matchesSearch && matchesType && matchesStatus && matchesRisk && matchesScore;
  });

  // ─── Chart data (computed over the full dataset) ───
  const inBracket = (similarity, bracket) => {
    const s = parseScore(similarity);
    if (bracket === 'none') return s === null;
    if (s === null) return false;
    if (bracket === '90-100') return s >= 90;
    if (bracket === '70-89') return s >= 70 && s < 90;
    if (bracket === '50-69') return s >= 50 && s < 70;
    if (bracket === 'below-50') return s < 50;
    return false;
  };

  const riskItems = RISK_ORDER
    .map(r => ({ label: r, filterVal: r, hex: RISK_HEX[r], value: domains.filter(d => d.risk === r).length }))
    .filter(i => i.value > 0);

  const statusItems = STATUS_ORDER
    .map(s => ({ label: s, filterVal: s, hex: STATUS_HEX[s], value: domains.filter(d => d.status === s).length }))
    .filter(i => i.value > 0);

  const typeCounts = {};
  domains.forEach(d => { const t = d.type || 'Other'; typeCounts[t] = (typeCounts[t] || 0) + 1; });
  const typeItems = Object.entries(typeCounts)
    .map(([t, v]) => ({ label: t, filterVal: t, hex: TYPE_HEX[t] || '#64748b', value: v }))
    .sort((a, b) => b.value - a.value);

  const scoreItems = SCORE_BRACKETS
    .map(b => ({ label: b.label, filterVal: b.filterVal, hex: b.hex, value: domains.filter(d => inBracket(d.similarity, b.filterVal)).length }))
    .filter(i => i.value > 0);

  const applyChartFilter = (key, val) => {
    if (key === 'risk') setFilterRisk(prev => prev === val ? 'all' : val);
    else if (key === 'type') setFilterType(prev => prev === val ? 'all' : val);
    else if (key === 'status') setFilterStatus(prev => prev === val ? 'all' : val);
    else if (key === 'score') setFilterScore(prev => prev === val ? 'all' : val);
  };

  // ─── Sort ───
  const sortedDomains = [...filteredDomains].sort((a, b) => {
    let aVal = a[sortField] || '';
    let bVal = b[sortField] || '';
    if (sortField === 'similarity') {
      aVal = parseScore(aVal) || 0;
      bVal = parseScore(bVal) || 0;
    }
    if (typeof aVal === 'string') { aVal = aVal.toLowerCase(); bVal = (bVal || '').toLowerCase(); }
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  // ─── Bulk selection ───
  const canBulkEdit = canEditIncidents;
  const visibleIds = sortedDomains.map(d => d.domain_id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some(id => selectedIds.has(id));
  const hasBulkChanges = Object.values(bulkEdits).some(Boolean);
  const bulkScoreInvalid = bulkEdits.similarity !== '' && normalizeScore(bulkEdits.similarity) === null;
  const bulkBusy = bulkSaving || bulkLookup !== null;

  const toggleSelect = (domainId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(domainId)) next.delete(domainId); else next.add(domainId);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleIds.forEach(id => next.delete(id));
      else visibleIds.forEach(id => next.add(id));
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setBulkEdits(EMPTY_BULK_EDITS);
  };

  // Selection is scoped to what is on screen — drop it when the filters change
  // so a bulk edit can never hit rows the user can no longer see.
  useEffect(() => {
    setSelectedIds(prev => (prev.size === 0 ? prev : new Set()));
    setBulkEdits(EMPTY_BULK_EDITS);
  }, [searchTerm, filterType, filterStatus, filterScore, filterRisk]);

  // ─── Bulk save (one PUT per domain; the API COALESCEs omitted fields) ───
  const handleBulkSave = async () => {
    const changes = Object.fromEntries(Object.entries(bulkEdits).filter(([, v]) => v));
    const ids = domains.filter(d => selectedIds.has(d.domain_id)).map(d => d.domain_id);
    if (!activeCompany || ids.length === 0 || Object.keys(changes).length === 0) return;

    if (changes.similarity) {
      const score = normalizeScore(changes.similarity);
      if (!score) { alert('Phishing score must be a number from 0 to 100 (e.g. 95 or 95%).'); return; }
      changes.similarity = score;
    }

    const summary = Object.entries(changes).map(([k, v]) => `${BULK_FIELD_LABELS[k] || k} → ${v}`).join(', ');
    if (!window.confirm(`Apply ${summary} to ${ids.length} domain${ids.length !== 1 ? 's' : ''}?`)) return;

    setBulkSaving(true);
    setBulkProgress({ done: 0, total: ids.length });
    const updates = [];
    const failures = [];

    try {
      const token = await getToken();
      let cursor = 0;
      const worker = async () => {
        while (cursor < ids.length) {
          const id = ids[cursor++];
          try {
            const res = await fetch(
              `/api/companies/${activeCompany.id}/impersonating-domains/${id}`,
              {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(changes)
              }
            );
            if (!res.ok) {
              const err = await res.json().catch(() => ({}));
              throw new Error(err.error || `HTTP ${res.status}`);
            }
            updates.push(await res.json());
          } catch (e) {
            failures.push(`${id}: ${e.message}`);
          } finally {
            setBulkProgress(p => ({ done: (p?.done || 0) + 1, total: ids.length }));
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(5, ids.length) }, worker));

      if (updates.length) {
        const byId = new Map(updates.map(u => [u.domain_id, u]));
        setDomains(prev => prev.map(d => byId.get(d.domain_id) || d));
      }

      if (failures.length) {
        alert(
          `Updated ${updates.length} of ${ids.length} domains.\n\nFailed:\n` +
          failures.slice(0, 10).join('\n') +
          (failures.length > 10 ? `\n…and ${failures.length - 10} more` : '')
        );
      } else {
        alert(`Updated ${updates.length} domain${updates.length !== 1 ? 's' : ''}.`);
      }
      clearSelection();
    } catch (e) {
      console.error('Bulk update error:', e);
      alert(`Bulk update failed: ${e.message}`);
    } finally {
      setBulkSaving(false);
      setBulkProgress(null);
    }
  };

  // ─── Bulk DNS / WHOIS enrichment (one POST per domain, same endpoints as the row buttons) ───
  const handleBulkLookup = async (kind) => {
    const cfg = BULK_LOOKUPS[kind];
    const ids = domains.filter(d => selectedIds.has(d.domain_id)).map(d => d.domain_id);
    if (!activeCompany || ids.length === 0) return;

    if (!window.confirm(
      `Run ${cfg.label} on ${ids.length} domain${ids.length !== 1 ? 's' : ''}?\n\n` +
      `This calls the external lookup API once per domain and may take a while.`
    )) return;

    setBulkLookup(kind);
    setBulkProgress({ done: 0, total: ids.length });
    const updates = [];
    const failures = [];
    let noRecord = 0;
    let stopReason = null;

    try {
      const token = await getToken();
      let cursor = 0;
      const worker = async () => {
        while (cursor < ids.length && !stopReason) {
          const id = ids[cursor++];
          try {
            const res = await fetch(
              `/api/companies/${activeCompany.id}/impersonating-domains/${id}/${cfg.path}`,
              { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
            );
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
              // Upstream quota / rate limit / missing key — the rest of the run would fail too.
              if (res.status === 429 || res.status === 503) {
                stopReason = data.error || `Lookup API unavailable (HTTP ${res.status})`;
              }
              throw new Error(data.error || `HTTP ${res.status}`);
            }
            if (kind === 'whois') {
              if (data.success === false || !data.domain) { noRecord++; continue; }
              updates.push(data.domain);
            } else {
              const { _dns_summary, ...updated } = data;
              updates.push(updated);
            }
          } catch (e) {
            failures.push(`${id}: ${e.message}`);
          } finally {
            setBulkProgress(p => ({ done: (p?.done || 0) + 1, total: ids.length }));
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(3, ids.length) }, worker));

      if (updates.length) {
        const byId = new Map(updates.map(u => [u.domain_id, u]));
        setDomains(prev => prev.map(d => byId.get(d.domain_id) || d));
      }

      const attempted = updates.length + noRecord + failures.length;
      let msg = `${cfg.label}: ${updates.length} of ${ids.length} domain${ids.length !== 1 ? 's' : ''} updated.`;
      if (noRecord) msg += `\n${noRecord} returned no record.`;
      if (stopReason) msg += `\n\nStopped early — ${stopReason}\n${ids.length - attempted} domain(s) not attempted.`;
      if (failures.length) {
        msg += `\n\nFailed:\n` + failures.slice(0, 10).join('\n') +
          (failures.length > 10 ? `\n…and ${failures.length - 10} more` : '');
      }
      alert(msg);
    } catch (e) {
      console.error(`Bulk ${kind} lookup error:`, e);
      alert(`Bulk ${cfg.label} failed: ${e.message}`);
    } finally {
      setBulkLookup(null);
      setBulkProgress(null);
    }
  };

  // ─── CSV Export ───
  const exportToCSV = (exportAll = false) => {
    const dataToExport = exportAll ? domains : filteredDomains;
    if (dataToExport.length === 0) { alert('No domains to export.'); return; }

    const headers = [
      'Domain ID', 'Domain', 'Type', 'Status', 'Risk', 'Phishing Score',
      'Registrar', 'IP Address', 'A Records', 'MX Records', 'NS Records',
      'Registered Date', 'Expiry Date', 'Registrant Name', 'Registrant Company',
      'Registrant Country', 'Nameservers', 'Notes', 'DNS Last Checked', 'WHOIS Last Checked',
      'Created At', 'Created By', 'Updated At', 'Updated By'
    ];

    const escapeCSV = (val) => {
      if (val === null || val === undefined) return '';
      const str = String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) return `"${str.replace(/"/g, '""')}"`;
      return str;
    };

    const formatArr = (arr) => Array.isArray(arr) ? arr.join('; ') : '';
    const formatMx = (mx) => Array.isArray(mx) ? mx.map(r => `${r.priority} ${r.exchange}`).join('; ') : '';

    const rows = dataToExport.map(d => [
      d.domain_id, d.domain, d.type, d.status, d.risk, formatScore(d.similarity) || '',
      d.registrar, d.ip_address, formatArr(d.a_records), formatMx(d.mx_records),
      formatArr(d.ns_records), d.registered, d.expiry_date,
      d.registrant_name, d.registrant_company, d.registrant_country,
      formatArr(d.nameservers), d.notes,
      d.dns_last_checked ? new Date(d.dns_last_checked).toLocaleString() : '',
      d.whois_last_checked ? new Date(d.whois_last_checked).toLocaleString() : '',
      d.created_at ? new Date(d.created_at).toLocaleString() : '',
      d.created_by,
      d.updated_at ? new Date(d.updated_at).toLocaleString() : '',
      d.updated_by
    ].map(escapeCSV));

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `impersonating_domains_${exportAll ? 'all' : 'filtered'}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportMenu(false);
  };

  // ─── Color helpers ───
  const getRiskColor = (risk) => ({
    Critical: 'bg-red-500/20 text-red-400 border-red-500/30',
    High: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    Medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    Low: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    Informational: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
  })[risk] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';

  const getStatusColor = (status) => {
    if (status === 'Active') return 'bg-red-500/20 text-red-400 border-red-500/30';
    if (status?.includes('Takedown')) return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    if (status?.includes('Resolved')) return 'bg-green-500/20 text-green-400 border-green-500/30';
    if (status === 'Monitoring') return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
    if (status === 'False Positive') return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  const getScoreBadgeColor = (similarity) => {
    const score = parseScore(similarity);
    if (score === null) return '';
    if (score >= 90) return 'text-red-400';
    if (score >= 70) return 'text-orange-400';
    if (score >= 50) return 'text-yellow-400';
    return 'text-blue-400';
  };

  const getRiskDotColor = (risk) => ({
    Critical: 'bg-red-500', High: 'bg-orange-500', Medium: 'bg-yellow-500',
    Low: 'bg-blue-500', Informational: 'bg-gray-500'
  })[risk] || 'bg-gray-500';

  // ─── Edit handlers ───
  const handleEdit = (domain) => { setEditedDomain({ ...domain }); setIsEditing(true); };
  const handleCancelEdit = () => { setEditedDomain(null); setIsEditing(false); };

  const handleSave = async () => {
    if (!editedDomain || !activeCompany) { alert('No domain selected or company context missing.'); return; }
    try {
      const token = await getToken();
      const response = await fetch(
        `/api/companies/${activeCompany.id}/impersonating-domains/${editedDomain.domain_id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            domain: editedDomain.domain, type: editedDomain.type, status: editedDomain.status,
            risk: editedDomain.risk, registered: editedDomain.registered, registrar: editedDomain.registrar,
            similarity: editedDomain.similarity, notes: editedDomain.notes, ip_address: editedDomain.ip_address,
            ...(canSeeSuperAdminSettings ? {
              screenshot_url: editedDomain.screenshot_url || null,
              urlscan_result_url: editedDomain.urlscan_result_url || null
            } : {})
          })
        }
      );
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `Failed to update domain (HTTP ${response.status})`);
      }
      const updated = await response.json();
      setDomains(domains.map(d => d.domain_id === updated.domain_id ? updated : d));
      setIsEditing(false);
      setEditedDomain(null);
      setExpandedRow(updated.domain_id);
      alert(`Domain "${updated.domain_id}" updated successfully!`);
    } catch (error) {
      console.error('Error saving domain:', error);
      alert(`Error: ${error.message}`);
    }
  };

  // ─── DNS Lookup ───
  const handleDnsLookup = async (domain) => {
    if (!activeCompany) return;
    setDnsLoading(domain.domain_id);
    try {
      const token = await getToken();
      const response = await fetch(
        `/api/companies/${activeCompany.id}/impersonating-domains/${domain.domain_id}/dns-lookup`,
        { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `DNS lookup failed (HTTP ${response.status})`);
      }
      const updated = await response.json();
      const s = updated._dns_summary;
      setDomains(domains.map(d => d.domain_id === updated.domain_id ? updated : d));

      let msg = `DNS Lookup complete for ${domain.domain}:\n`;
      msg += `• A Records: ${s.a_count} found\n`;
      msg += `• MX Records: ${s.mx_count} found\n`;
      msg += `• NS Records: ${s.ns_count} found\n`;
      msg += `• TXT Records: ${s.txt_count} found`;
      if (s.errors) msg += `\n\nErrors:\n${s.errors.join('\n')}`;
      alert(msg);
    } catch (error) {
      console.error('DNS lookup error:', error);
      alert(`DNS Lookup failed: ${error.message}`);
    } finally {
      setDnsLoading(null);
    }
  };

  // ─── WHOIS Lookup ───
  const handleWhoisLookup = async (domain) => {
    if (!activeCompany) return;
    setWhoisLoading(domain.domain_id);
    try {
      const token = await getToken();
      const response = await fetch(
        `/api/companies/${activeCompany.id}/impersonating-domains/${domain.domain_id}/whois-lookup`,
        { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || `WHOIS lookup failed (HTTP ${response.status})`);
      }
      if (data.success === false) {
        alert(`WHOIS Lookup: ${data.message || 'WHOIS record not found'}`);
        return;
      }
      const updated = data.domain;
      setDomains(domains.map(d => d.domain_id === updated.domain_id ? updated : d));

      const s = data.whois_summary || {};
      let msg = `WHOIS Lookup complete for ${domain.domain}:\n`;
      msg += `• Registrar: ${s.registrar || 'N/A'}\n`;
      msg += `• Created: ${s.create_date || 'N/A'}\n`;
      msg += `• Expires: ${s.expiry_date || 'N/A'}\n`;
      msg += `• Registrant: ${s.registrant_name || 'N/A'}\n`;
      msg += `• Organization: ${s.registrant_company || 'N/A'}\n`;
      msg += `• Country: ${s.registrant_country || 'N/A'}`;
      alert(msg);
    } catch (error) {
      console.error('WHOIS lookup error:', error);
      alert(`WHOIS Lookup failed: ${error.message}`);
    } finally {
      setWhoisLoading(null);
    }
  };

  // ─── urlscan Screenshot Capture ───
  const handleUrlscanScan = async (domain) => {
    if (!activeCompany) return;
    setUrlscanLoading(domain.domain_id);
    try {
      const token = await getToken();
      const response = await fetch(
        `/api/companies/${activeCompany.id}/impersonating-domains/${domain.domain_id}/urlscan-scan`,
        { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || `urlscan capture failed (HTTP ${response.status})`);
      }
      const { _urlscan_summary: s, ...updated } = data;
      setDomains(domains.map(d => d.domain_id === updated.domain_id ? updated : d));

      let msg = `urlscan capture complete for ${domain.domain}:\n`;
      msg += `• Page title: ${s?.page_title || 'N/A'}\n`;
      msg += `• IP: ${s?.page_ip || 'N/A'} (${s?.page_country || '?'})\n`;
      msg += `• HTTP status: ${s?.page_status || 'N/A'}`;
      if (s && !s.completed) {
        msg += `\n\nScan is still processing on urlscan.io — the screenshot will appear once it finishes (refresh in a minute).`;
      }
      alert(msg);
    } catch (error) {
      console.error('urlscan capture error:', error);
      alert(`urlscan capture failed: ${error.message}`);
    } finally {
      setUrlscanLoading(null);
    }
  };

  // ─── Toggle expand ───
  const toggleExpand = (domainId) => {
    if (expandedRow === domainId) {
      setExpandedRow(null); setIsEditing(false); setEditedDomain(null); setShowDnsExtra(false);
    } else {
      setExpandedRow(domainId); setIsEditing(false); setEditedDomain(null); setShowDnsExtra(false);
    }
  };

  // ─── Sortable header ───
  const SortHeader = ({ field, children }) => (
    <th onClick={() => toggleSort(field)} className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider cursor-pointer hover:text-cyan-400 transition-colors select-none whitespace-nowrap">
      <div className="flex items-center gap-1.5">
        {children}
        <ArrowUpDown className={`w-3 h-3 flex-shrink-0 ${sortField === field ? 'text-cyan-400' : 'text-slate-600'}`} />
      </div>
    </th>
  );

  // ═══════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════
  return (
    <div className="h-full flex flex-col">
      {/* HEADER */}
      <div className="px-6 py-4 border-b border-slate-800/50 bg-gradient-to-r from-slate-900/50 to-slate-900/30">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-bold text-slate-100 flex items-center gap-2">
              <Globe className="w-5 h-5 text-cyan-400" />
              Impersonating Domains
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Review and manage detected suspicious domains for {activeCompany?.name || 'your company'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Export */}
            <div className="relative" ref={exportMenuRef}>
              <button onClick={() => setShowExportMenu(!showExportMenu)} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-xs text-slate-200 font-medium transition-all flex items-center gap-1.5" title="Export to CSV">
                <Download className="w-3.5 h-3.5" /> Export
              </button>
              {showExportMenu && (
                <div className="absolute right-0 top-full mt-1 bg-slate-800 border border-slate-700/50 rounded-lg shadow-xl z-50 min-w-[180px] overflow-hidden">
                  <button onClick={() => exportToCSV(false)} className="w-full px-4 py-2.5 text-left text-sm text-slate-200 hover:bg-slate-700/50 transition-colors flex items-center justify-between">
                    <span>Export Filtered</span><span className="text-xs text-slate-400">{filteredDomains.length}</span>
                  </button>
                  <div className="border-t border-slate-700/30" />
                  <button onClick={() => exportToCSV(true)} className="w-full px-4 py-2.5 text-left text-sm text-slate-200 hover:bg-slate-700/50 transition-colors flex items-center justify-between">
                    <span>Export All</span><span className="text-xs text-slate-400">{domains.length}</span>
                  </button>
                </div>
              )}
            </div>
            {canSeeSuperAdminSettings && (
              <button onClick={() => setShowAddImpersonatingDomain(true)} className="px-3 py-1.5 bg-cyan-500 hover:bg-cyan-600 rounded-lg text-xs text-white font-medium transition-all flex items-center gap-1.5">
                <Plus className="w-3.5 h-3.5" /> New
              </button>
            )}
          </div>
        </div>

        {/* FILTERS */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type="text" placeholder="Search domain, ID, or IP..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-9 pr-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20" />
          </div>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="px-2 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-xs text-slate-200">
            <option value="all">All Types</option>
            <option value="Typosquatting">Typosquatting</option>
            <option value="Homograph Attack">Homograph Attack</option>
            <option value="Combosquatting">Combosquatting</option>
            <option value="Brand Jacking">Brand Jacking</option>
            <option value="Phishing">Phishing</option>
            <option value="Look-alike Domain">Look-alike Domain</option>
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-2 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-xs text-slate-200">
            <option value="all">All Status</option>
            <option value="Active">Active</option>
            <option value="Monitoring">Monitoring</option>
            <option value="Takedown Requested">Takedown Requested</option>
            <option value="Takedown Successful">Takedown Successful</option>
            <option value="Resolved">Resolved</option>
            <option value="False Positive">False Positive</option>
          </select>
          <select value={filterScore} onChange={(e) => setFilterScore(e.target.value)} className="px-2 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-xs text-slate-200">
            <option value="all">All Scores</option>
            <option value="90-100">90-100% (Critical)</option>
            <option value="70-89">70-89% (High)</option>
            <option value="50-69">50-69% (Medium)</option>
            <option value="below-50">Below 50%</option>
            <option value="none">No Score</option>
          </select>
          <div className="text-xs text-slate-400 ml-auto">
            {filteredDomains.length} domain{filteredDomains.length !== 1 ? 's' : ''}
            {(filterType !== 'all' || filterStatus !== 'all' || filterScore !== 'all' || filterRisk !== 'all' || searchTerm) && (
              <span className="text-slate-500"> (filtered from {domains.length})</span>
            )}
          </div>
        </div>
      </div>

      {/* STATS / CHARTS */}
      {activeCompany && !loading && !error && domains.length > 0 && (
        <div className="px-6 pt-4 border-b border-slate-800/50">
          <button
            onClick={() => setShowCharts(s => !s)}
            className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-cyan-400 uppercase tracking-wider mb-3 transition-colors"
          >
            <PieChart className="w-3.5 h-3.5" />
            Overview
            {showCharts ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
          {showCharts && (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 pb-4">
              <DonutChart title="Risk Breakdown" items={riskItems} total={domains.length} activeValue={filterRisk} filterKey="risk" onSegment={applyChartFilter} />
              <DonutChart title="Status" items={statusItems} total={domains.length} activeValue={filterStatus} filterKey="status" onSegment={applyChartFilter} />
              <DonutChart title="Type" items={typeItems} total={domains.length} activeValue={filterType} filterKey="type" onSegment={applyChartFilter} />
              <DonutChart title="Phishing Score" items={scoreItems} total={domains.length} activeValue={filterScore} filterKey="score" onSegment={applyChartFilter} />
            </div>
          )}
        </div>
      )}

      {/* BULK ACTION BAR */}
      {canBulkEdit && selectedIds.size > 0 && (
        <div className="px-6 py-3 border-b border-cyan-500/20 bg-cyan-500/5 flex items-center gap-2 flex-wrap">
          <span className="flex items-center gap-1.5 text-xs font-semibold text-cyan-300">
            <Layers className="w-3.5 h-3.5" />
            {selectedIds.size} selected
          </span>
          <select
            value={bulkEdits.type}
            onChange={(e) => setBulkEdits({ ...bulkEdits, type: e.target.value })}
            disabled={bulkBusy}
            className="px-2 py-1.5 bg-slate-800/70 border border-slate-700/50 rounded-lg text-xs text-slate-200 disabled:opacity-50"
          >
            <option value="">Type — unchanged</option>
            <option value="Typosquatting">Typosquatting</option>
            <option value="Homograph Attack">Homograph Attack</option>
            <option value="Combosquatting">Combosquatting</option>
            <option value="Brand Jacking">Brand Jacking</option>
            <option value="Phishing">Phishing</option>
            <option value="Look-alike Domain">Look-alike Domain</option>
            <option value="Other">Other</option>
          </select>
          <select
            value={bulkEdits.status}
            onChange={(e) => setBulkEdits({ ...bulkEdits, status: e.target.value })}
            disabled={bulkBusy}
            className="px-2 py-1.5 bg-slate-800/70 border border-slate-700/50 rounded-lg text-xs text-slate-200 disabled:opacity-50"
          >
            <option value="">Status — unchanged</option>
            <option value="Active">Active</option>
            <option value="Monitoring">Monitoring</option>
            <option value="Takedown Requested">Takedown Requested</option>
            <option value="Takedown Successful">Takedown Successful</option>
            <option value="Resolved">Resolved</option>
            <option value="False Positive">False Positive</option>
          </select>
          <select
            value={bulkEdits.risk}
            onChange={(e) => setBulkEdits({ ...bulkEdits, risk: e.target.value })}
            disabled={bulkBusy}
            className="px-2 py-1.5 bg-slate-800/70 border border-slate-700/50 rounded-lg text-xs text-slate-200 disabled:opacity-50"
          >
            <option value="">Risk — unchanged</option>
            <option value="Critical">Critical</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
            <option value="Informational">Informational</option>
          </select>
          <input
            type="text"
            inputMode="decimal"
            value={bulkEdits.similarity}
            onChange={(e) => setBulkEdits({ ...bulkEdits, similarity: e.target.value })}
            disabled={bulkBusy}
            placeholder="Score — unchanged"
            title="Phishing score, 0–100 (e.g. 95)"
            className={`w-40 px-2 py-1.5 bg-slate-800/70 border rounded-lg text-xs text-slate-200 placeholder-slate-500 focus:outline-none disabled:opacity-50 ${
              bulkScoreInvalid ? 'border-red-500/60 focus:border-red-500' : 'border-slate-700/50 focus:border-cyan-500/50'
            }`}
          />
          <button
            onClick={handleBulkSave}
            disabled={bulkBusy || !hasBulkChanges || bulkScoreInvalid}
            title={bulkScoreInvalid ? 'Phishing score must be a number from 0 to 100' : undefined}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
              bulkBusy || !hasBulkChanges || bulkScoreInvalid
                ? 'bg-slate-700/50 text-slate-500 cursor-not-allowed'
                : 'bg-cyan-500 hover:bg-cyan-600 text-white'
            }`}
          >
            {bulkSaving
              ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Saving {bulkProgress?.done ?? 0}/{bulkProgress?.total ?? selectedIds.size}...</>
              : <><Save className="w-3.5 h-3.5" /> Apply to {selectedIds.size}</>}
          </button>
          {canSeeSuperAdminSettings && (
            <>
              <span className="w-px h-5 bg-slate-700/70" />
              <button
                onClick={() => handleBulkLookup('dns')}
                disabled={bulkBusy}
                title="Run a DNS lookup on every selected domain"
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 border ${
                  bulkBusy
                    ? 'bg-slate-700/50 text-slate-500 border-slate-700/50 cursor-not-allowed'
                    : 'bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 border-cyan-500/30 hover:border-cyan-500/50'
                }`}
              >
                <RefreshCw className={`w-3.5 h-3.5 ${bulkLookup === 'dns' ? 'animate-spin' : ''}`} />
                {bulkLookup === 'dns'
                  ? `DNS ${bulkProgress?.done ?? 0}/${bulkProgress?.total ?? selectedIds.size}...`
                  : `DNS Lookup (${selectedIds.size})`}
              </button>
              <button
                onClick={() => handleBulkLookup('whois')}
                disabled={bulkBusy}
                title="Run a WHOIS lookup on every selected domain"
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 border ${
                  bulkBusy
                    ? 'bg-slate-700/50 text-slate-500 border-slate-700/50 cursor-not-allowed'
                    : 'bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 border-purple-500/30 hover:border-purple-500/50'
                }`}
              >
                <Shield className={`w-3.5 h-3.5 ${bulkLookup === 'whois' ? 'animate-pulse' : ''}`} />
                {bulkLookup === 'whois'
                  ? `WHOIS ${bulkProgress?.done ?? 0}/${bulkProgress?.total ?? selectedIds.size}...`
                  : `Update WHOIS (${selectedIds.size})`}
              </button>
            </>
          )}
          <button
            onClick={clearSelection}
            disabled={bulkBusy}
            className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-xs text-slate-200 transition-all flex items-center gap-1.5 disabled:opacity-50"
          >
            <X className="w-3.5 h-3.5" /> Clear
          </button>
        </div>
      )}

      {/* TABLE */}
      <div className="flex-1 overflow-auto">
        {!activeCompany && <div className="p-8 text-center text-slate-400">No company selected.</div>}
        {error && <div className="m-4 bg-red-500/10 border border-red-500/30 rounded p-3 text-sm text-red-300">{error}</div>}
        {loading && <div className="p-8 text-center text-slate-400">Loading domains...</div>}

        {!loading && activeCompany && !error && (
          <table className="w-full">
            <thead className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800/50">
              <tr>
                {canBulkEdit && (
                  <th className="w-10 px-3 py-3">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      ref={el => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
                      onChange={toggleSelectAllVisible}
                      title="Select all shown"
                      className="w-4 h-4 align-middle accent-cyan-500 cursor-pointer"
                    />
                  </th>
                )}
                <th className="w-10 px-3 py-3"></th>
                <SortHeader field="domain">Domain</SortHeader>
                <SortHeader field="type">Type</SortHeader>
                <SortHeader field="ip_address">IP Address</SortHeader>
                <SortHeader field="registrar">Registrar</SortHeader>
                <SortHeader field="registered">Registered</SortHeader>
                <SortHeader field="similarity">Phishing Score</SortHeader>
                <SortHeader field="risk">Risk</SortHeader>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/30">
              {sortedDomains.length === 0 && (
                <tr><td colSpan={canBulkEdit ? 10 : 9} className="px-4 py-16 text-center">
                  <Globe className="w-12 h-12 text-slate-700 mx-auto mb-3" />
                  <p className="text-slate-500">No domains found matching your filters.</p>
                </td></tr>
              )}

              {sortedDomains.map((domain) => {
                const isExpanded = expandedRow === domain.domain_id;
                const isSelected = selectedIds.has(domain.domain_id);
                const isDnsLoading = dnsLoading === domain.domain_id;
                const isWhoisLoading = whoisLoading === domain.domain_id;
                const isUrlscanLoading = urlscanLoading === domain.domain_id;

                return (
                  <React.Fragment key={domain.domain_id}>
                    {/* TABLE ROW */}
                    <tr onClick={() => toggleExpand(domain.domain_id)} className={`cursor-pointer transition-colors ${isSelected ? 'bg-cyan-500/10' : isExpanded ? 'bg-cyan-500/5' : 'hover:bg-slate-800/30'}`}>
                      {canBulkEdit && (
                        <td className="px-3 py-3.5" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelect(domain.domain_id)}
                            className="w-4 h-4 align-middle accent-cyan-500 cursor-pointer"
                          />
                        </td>
                      )}
                      <td className="px-3 py-3.5">
                        {isExpanded ? <ChevronDown className="w-4 h-4 text-cyan-400" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <Globe className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                          <div>
                            <div className="text-sm font-medium text-slate-200 font-mono">{domain.domain}</div>
                            <div className="text-xs text-slate-500 mt-0.5">{domain.domain_id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5"><span className="text-xs text-slate-400 bg-slate-800/40 px-2 py-0.5 rounded">{domain.type || '-'}</span></td>
                      <td className="px-4 py-3.5 text-sm text-slate-300 font-mono">{domain.ip_address || '—'}</td>
                      <td className="px-4 py-3.5 text-sm text-slate-300">{domain.registrar || '—'}</td>
                      <td className="px-4 py-3.5 text-sm text-slate-400">{domain.registered ? new Date(domain.registered).toLocaleDateString() : '—'}</td>
                      <td className="px-4 py-3.5"><span className={`text-sm font-semibold font-mono ${getScoreBadgeColor(domain.similarity)}`}>{formatScore(domain.similarity) || '—'}</span></td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded text-xs font-medium border ${getRiskColor(domain.risk)}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${getRiskDotColor(domain.risk)}`}></span>
                          {domain.risk || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium border ${getStatusColor(domain.status)}`}>{domain.status || 'Unknown'}</span>
                      </td>
                    </tr>

                    {/* EXPANDED DETAIL */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={canBulkEdit ? 10 : 9} className="bg-slate-800/15 border-t border-slate-700/30">
                          <div className="px-8 py-6">
                            {/* Row 1: Core details */}
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                              <DetailCard label="Registrar">
                                {isEditing ? <input type="text" value={editedDomain?.registrar || ''} onChange={e => setEditedDomain({ ...editedDomain, registrar: e.target.value })} className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-1.5 text-sm text-slate-200" placeholder="e.g., Namecheap" />
                                : <span className="text-slate-200">{domain.registrar || '-'}</span>}
                              </DetailCard>
                              <DetailCard label="IP Address">
                                {isEditing ? <input type="text" value={editedDomain?.ip_address || ''} onChange={e => setEditedDomain({ ...editedDomain, ip_address: e.target.value })} className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-1.5 text-sm text-slate-200 font-mono" placeholder="e.g., 192.168.1.1" />
                                : <span className="text-slate-200 font-mono">{domain.ip_address || '-'}</span>}
                              </DetailCard>
                              <DetailCard label="Registered Date">
                                {isEditing ? <input type="date" value={editedDomain?.registered || ''} onChange={e => setEditedDomain({ ...editedDomain, registered: e.target.value })} className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-1.5 text-sm text-slate-200" />
                                : <span className="text-slate-200">{domain.registered ? new Date(domain.registered).toLocaleDateString() : '-'}</span>}
                              </DetailCard>
                              <DetailCard label="Phishing Score">
                                {isEditing ? <input type="text" value={editedDomain?.similarity || ''} onChange={e => setEditedDomain({ ...editedDomain, similarity: e.target.value })} className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-1.5 text-sm text-slate-200" placeholder="e.g., 95" />
                                : <span className={`font-semibold ${getScoreBadgeColor(domain.similarity)}`}>{formatScore(domain.similarity) || '-'}</span>}
                              </DetailCard>
                            </div>

                            {/* Edit-only: Type/Status/Risk/Domain */}
                            {isEditing && (
                              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                                <DetailCard label="Type">
                                  <select value={editedDomain?.type || ''} onChange={e => setEditedDomain({ ...editedDomain, type: e.target.value })} className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-1.5 text-sm text-slate-200">
                                    <option value="Typosquatting">Typosquatting</option><option value="Homograph Attack">Homograph Attack</option><option value="Combosquatting">Combosquatting</option>
                                    <option value="Brand Jacking">Brand Jacking</option><option value="Phishing">Phishing</option><option value="Look-alike Domain">Look-alike Domain</option><option value="Other">Other</option>
                                  </select>
                                </DetailCard>
                                <DetailCard label="Status">
                                  <select value={editedDomain?.status || ''} onChange={e => setEditedDomain({ ...editedDomain, status: e.target.value })} className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-1.5 text-sm text-slate-200">
                                    <option value="Active">Active</option><option value="Monitoring">Monitoring</option><option value="Takedown Requested">Takedown Requested</option>
                                    <option value="Takedown Successful">Takedown Successful</option><option value="Resolved">Resolved</option><option value="False Positive">False Positive</option>
                                  </select>
                                </DetailCard>
                                <DetailCard label="Risk Level">
                                  <select value={editedDomain?.risk || ''} onChange={e => setEditedDomain({ ...editedDomain, risk: e.target.value })} className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-1.5 text-sm text-slate-200">
                                    <option value="Critical">Critical</option><option value="High">High</option><option value="Medium">Medium</option><option value="Low">Low</option><option value="Informational">Informational</option>
                                  </select>
                                </DetailCard>
                                <DetailCard label="Domain Name">
                                  <input type="text" value={editedDomain?.domain || ''} onChange={e => setEditedDomain({ ...editedDomain, domain: e.target.value })} className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-1.5 text-sm text-slate-200 font-mono" />
                                </DetailCard>
                              </div>
                            )}

                            {/* DNS Records Section */}
                            {(domain.a_records || domain.mx_records || domain.dns_last_checked) && (
                              <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4 mb-5">
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className="text-xs font-semibold text-cyan-400 uppercase tracking-wider">DNS Records</h4>
                                  {domain.dns_last_checked && (
                                    <span className="text-xs text-slate-500">
                                      Last checked: {new Date(domain.dns_last_checked).toLocaleString()}
                                    </span>
                                  )}
                                </div>

                                <div className="grid grid-cols-2 gap-4 mb-3">
                                  {/* A Records */}
                                  <div>
                                    <div className="text-xs font-medium text-slate-400 mb-1.5">A Records (IPv4)</div>
                                    {domain.a_records && domain.a_records.length > 0 ? (
                                      <div className="space-y-1">
                                        {domain.a_records.map((ip, i) => (
                                          <div key={i} className="text-sm text-slate-200 font-mono bg-slate-900/40 px-2.5 py-1 rounded inline-block mr-2 mb-1">{ip}</div>
                                        ))}
                                      </div>
                                    ) : (
                                      <span className="text-xs text-slate-500 italic">No A records found</span>
                                    )}
                                  </div>

                                  {/* MX Records */}
                                  <div>
                                    <div className="text-xs font-medium text-slate-400 mb-1.5">MX Records (Mail)</div>
                                    {domain.mx_records && domain.mx_records.length > 0 ? (
                                      <div className="space-y-1">
                                        {domain.mx_records.map((mx, i) => (
                                          <div key={i} className="text-sm text-slate-200 flex items-center gap-2">
                                            <span className="text-xs text-slate-500 bg-slate-900/40 px-1.5 py-0.5 rounded font-mono min-w-[32px] text-center">{mx.priority}</span>
                                            <span className="font-mono text-xs">{mx.exchange}</span>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <span className="text-xs text-slate-500 italic">No MX records found</span>
                                    )}
                                  </div>
                                </div>

                                {/* NS + TXT toggle */}
                                {(domain.ns_records?.length > 0 || domain.txt_records?.length > 0) && (
                                  <>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setShowDnsExtra(!showDnsExtra); }}
                                      className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors flex items-center gap-1 mt-2"
                                    >
                                      {showDnsExtra ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                                      {showDnsExtra ? 'Hide' : 'Show'} NS & TXT Records
                                    </button>

                                    {showDnsExtra && (
                                      <div className="grid grid-cols-2 gap-4 mt-3 pt-3 border-t border-slate-700/30">
                                        {/* NS Records */}
                                        <div>
                                          <div className="text-xs font-medium text-slate-400 mb-1.5">NS Records (Nameservers)</div>
                                          {domain.ns_records?.length > 0 ? (
                                            <div className="space-y-1">
                                              {domain.ns_records.map((ns, i) => (
                                                <div key={i} className="text-xs text-slate-300 font-mono">{ns}</div>
                                              ))}
                                            </div>
                                          ) : <span className="text-xs text-slate-500 italic">None</span>}
                                        </div>

                                        {/* TXT Records */}
                                        <div>
                                          <div className="text-xs font-medium text-slate-400 mb-1.5">TXT Records</div>
                                          {domain.txt_records?.length > 0 ? (
                                            <div className="space-y-1">
                                              {domain.txt_records.map((txt, i) => (
                                                <div key={i} className="text-xs text-slate-300 font-mono break-all bg-slate-900/30 px-2 py-1 rounded">{txt}</div>
                                              ))}
                                            </div>
                                          ) : <span className="text-xs text-slate-500 italic">None</span>}
                                        </div>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            )}

                            {/* WHOIS Intelligence Section */}
                            {(domain.expiry_date || domain.registrant_name || domain.registrant_company || domain.registrant_country || (domain.nameservers && domain.nameservers.length > 0) || domain.whois_last_checked) && (
                              <div className="bg-slate-800/30 rounded-lg border border-cyan-500/20 p-4 mb-5">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    <Shield className="w-4 h-4 text-cyan-400" />
                                    <h4 className="text-xs font-semibold text-cyan-400 uppercase tracking-wider">WHOIS Intelligence</h4>
                                  </div>
                                  {domain.whois_last_checked && (
                                    <span className="text-xs text-slate-500">
                                      Last checked: {new Date(domain.whois_last_checked).toLocaleString()}
                                    </span>
                                  )}
                                </div>

                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                                  {domain.expiry_date && (
                                    <div>
                                      <div className="text-xs font-medium text-slate-400 mb-1 flex items-center gap-1.5">
                                        <Calendar className="w-3 h-3" />
                                        Expiry Date
                                      </div>
                                      <div className="text-sm text-slate-200">{new Date(domain.expiry_date).toLocaleDateString()}</div>
                                    </div>
                                  )}
                                  {domain.registrant_name && (
                                    <div>
                                      <div className="text-xs font-medium text-slate-400 mb-1">Registrant</div>
                                      <div className="text-sm text-slate-200 truncate" title={domain.registrant_name}>{domain.registrant_name}</div>
                                    </div>
                                  )}
                                  {domain.registrant_company && (
                                    <div>
                                      <div className="text-xs font-medium text-slate-400 mb-1">Organization</div>
                                      <div className="text-sm text-slate-200 truncate" title={domain.registrant_company}>{domain.registrant_company}</div>
                                    </div>
                                  )}
                                  {domain.registrant_country && (
                                    <div>
                                      <div className="text-xs font-medium text-slate-400 mb-1">Country</div>
                                      <div className="text-sm text-slate-200">{domain.registrant_country}</div>
                                    </div>
                                  )}
                                </div>

                                {domain.nameservers && domain.nameservers.length > 0 && (
                                  <div className="mt-3 pt-3 border-t border-slate-700/30">
                                    <div className="text-xs font-medium text-slate-400 mb-1.5 flex items-center gap-1.5">
                                      <Server className="w-3 h-3" />
                                      Nameservers
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      {domain.nameservers.map((ns, i) => (
                                        <span key={i} className="px-2 py-0.5 bg-slate-900/40 rounded text-xs text-slate-300 font-mono">{ns}</span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Site Screenshot — super-admin analysts only for now */}
                            {canSeeSuperAdminSettings && (domain.screenshot_url || domain.urlscan_result_url || isEditing) && (
                              <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4 mb-5">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    <Camera className="w-4 h-4 text-cyan-400" />
                                    <h4 className="text-xs font-semibold text-cyan-400 uppercase tracking-wider">Site Screenshot</h4>
                                  </div>
                                  {domain.screenshot_captured_at && (
                                    <span className="text-xs text-slate-500">
                                      Captured: {new Date(domain.screenshot_captured_at).toLocaleString()}
                                    </span>
                                  )}
                                </div>

                                {isEditing ? (
                                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    <div>
                                      <div className="text-xs font-medium text-slate-400 mb-1.5">Screenshot URL</div>
                                      <input type="url" value={editedDomain?.screenshot_url || ''} onChange={e => setEditedDomain({ ...editedDomain, screenshot_url: e.target.value })} className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-1.5 text-sm text-slate-200 font-mono" placeholder="https://urlscan.io/screenshots/<uuid>.png" />
                                    </div>
                                    <div>
                                      <div className="text-xs font-medium text-slate-400 mb-1.5">urlscan Report URL</div>
                                      <input type="url" value={editedDomain?.urlscan_result_url || ''} onChange={e => setEditedDomain({ ...editedDomain, urlscan_result_url: e.target.value })} className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-1.5 text-sm text-slate-200 font-mono" placeholder="https://urlscan.io/result/<uuid>/" />
                                    </div>
                                  </div>
                                ) : (
                                  <div className="space-y-3">
                                    {domain.screenshot_url && (
                                      <a href={domain.screenshot_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="block w-fit" title="Open full-size screenshot">
                                        <img
                                          src={domain.screenshot_url}
                                          alt={`Screenshot of ${domain.domain}`}
                                          className="max-h-64 rounded-lg border border-slate-700/50 hover:border-cyan-500/50 transition-colors"
                                          onError={e => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling && (e.currentTarget.nextSibling.style.display = 'block'); }}
                                        />
                                        <span style={{ display: 'none' }} className="text-xs text-slate-500 italic">Screenshot not available yet — the scan may still be processing.</span>
                                      </a>
                                    )}
                                    {domain.urlscan_result_url && (
                                      <a href={domain.urlscan_result_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="inline-flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300 transition-colors">
                                        <ExternalLink className="w-3 h-3" />
                                        Full urlscan.io report
                                      </a>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Notes */}
                            {(domain.notes || isEditing) && (
                              <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4 mb-5">
                                <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Notes</h4>
                                {isEditing ? <textarea rows={3} value={editedDomain?.notes || ''} onChange={e => setEditedDomain({ ...editedDomain, notes: e.target.value })} className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-2 text-sm text-slate-200" placeholder="Additional details about this domain..." />
                                : <p className="text-sm text-slate-200 whitespace-pre-wrap">{domain.notes}</p>}
                              </div>
                            )}

                            {/* Metadata */}
                            <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4 mb-5">
                              <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Metadata</h4>
                              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                                <div><span className="text-slate-500">Created:</span><span className="ml-2 text-slate-200">{domain.created_at ? new Date(domain.created_at).toLocaleString() : '-'}</span></div>
                                <div><span className="text-slate-500">Created By:</span><span className="ml-2 text-slate-200">{domain.created_by || '-'}</span></div>
                                <div><span className="text-slate-500">Updated:</span><span className="ml-2 text-slate-200">{domain.updated_at ? new Date(domain.updated_at).toLocaleString() : '-'}</span></div>
                                <div><span className="text-slate-500">Updated By:</span><span className="ml-2 text-slate-200">{domain.updated_by || '-'}</span></div>
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center gap-3">
                              {canEditIncidents && (
                                <>
                                  {isEditing ? (
                                    <>
                                      <button onClick={handleSave} className="px-4 py-2 bg-green-500 hover:bg-green-600 rounded-lg text-sm text-white transition-all flex items-center gap-2">
                                        <Save className="w-4 h-4" /> Save Changes
                                      </button>
                                      <button onClick={handleCancelEdit} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm text-slate-200 transition-all">Cancel</button>
                                    </>
                                  ) : (
                                    <button onClick={(e) => { e.stopPropagation(); handleEdit(domain); }} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm text-slate-200 transition-all flex items-center gap-2">
                                      <Edit className="w-4 h-4" /> Edit Domain
                                    </button>
                                  )}
                                </>
                              )}

                              {/* DNS Lookup Button — admin only */}
                              {canSeeSuperAdminSettings && !isEditing && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleDnsLookup(domain); }}
                                  disabled={isDnsLoading}
                                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                                    isDnsLoading
                                      ? 'bg-cyan-500/20 text-cyan-400 cursor-wait'
                                      : 'bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 hover:border-cyan-500/50'
                                  }`}
                                >
                                  <RefreshCw className={`w-4 h-4 ${isDnsLoading ? 'animate-spin' : ''}`} />
                                  {isDnsLoading ? 'Looking up...' : (domain.dns_last_checked ? 'Refresh DNS' : 'DNS Lookup')}
                                </button>
                              )}

                              {/* WHOIS Lookup Button — admin only */}
                              {canSeeSuperAdminSettings && !isEditing && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleWhoisLookup(domain); }}
                                  disabled={isWhoisLoading}
                                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                                    isWhoisLoading
                                      ? 'bg-purple-500/20 text-purple-400 cursor-wait'
                                      : 'bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:border-purple-500/50'
                                  }`}
                                  title="Fetch live WHOIS data (registrar, expiry, registrant)"
                                >
                                  <Shield className={`w-4 h-4 ${isWhoisLoading ? 'animate-pulse' : ''}`} />
                                  {isWhoisLoading ? 'Looking up...' : (domain.whois_last_checked ? 'Refresh WHOIS' : 'Update WHOIS')}
                                </button>
                              )}

                              {/* urlscan Screenshot Capture Button — admin only */}
                              {canSeeSuperAdminSettings && !isEditing && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleUrlscanScan(domain); }}
                                  disabled={isUrlscanLoading}
                                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                                    isUrlscanLoading
                                      ? 'bg-emerald-500/20 text-emerald-400 cursor-wait'
                                      : 'bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:border-emerald-500/50'
                                  }`}
                                  title="Submit an unlisted urlscan.io scan and save the screenshot (~30s)"
                                >
                                  <Camera className={`w-4 h-4 ${isUrlscanLoading ? 'animate-pulse' : ''}`} />
                                  {isUrlscanLoading ? 'Scanning (~30s)...' : (domain.screenshot_url ? 'Refresh Screenshot' : 'Capture Screenshot')}
                                </button>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ─── Donut chart with clickable segments + legend ───
const DonutChart = ({ title, items, total, activeValue, filterKey, onSegment }) => {
  const size = 128;
  const stroke = 16;
  const radius = (size - stroke) / 2;
  const circ = 2 * Math.PI * radius;
  const sum = items.reduce((a, i) => a + i.value, 0);
  const gap = sum > 0 && items.length > 1 ? 2 : 0;
  let acc = 0;

  return (
    <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4 flex flex-col">
      <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">{title}</h4>
      <div className="flex items-center gap-4">
        <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
          <svg width={size} height={size} className="-rotate-90">
            <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#1e293b" strokeWidth={stroke} />
            {sum > 0 && items.map((it, i) => {
              const frac = it.value / sum;
              const len = frac * circ;
              const dash = Math.max(len - gap, 0.001);
              const dimmed = activeValue !== 'all' && activeValue !== it.filterVal;
              const seg = (
                <circle
                  key={i}
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke={it.hex}
                  strokeWidth={stroke}
                  strokeDasharray={`${dash} ${circ - dash}`}
                  strokeDashoffset={-acc}
                  onClick={() => onSegment(filterKey, it.filterVal)}
                  className={`cursor-pointer transition-opacity ${dimmed ? 'opacity-25' : 'hover:opacity-80'}`}
                >
                  <title>{`${it.label}: ${it.value} (${Math.round(frac * 100)}%)`}</title>
                </circle>
              );
              acc += len;
              return seg;
            })}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-2xl font-bold text-slate-100">{total}</span>
            <span className="text-[10px] text-slate-500 uppercase tracking-wider">Total</span>
          </div>
        </div>
        <div className="flex-1 min-w-0 space-y-1">
          {items.length === 0 && <span className="text-xs text-slate-500 italic">No data</span>}
          {items.map((it, i) => {
            const isActive = activeValue === it.filterVal;
            const isDim = activeValue !== 'all' && !isActive;
            const pct = sum > 0 ? Math.round((it.value / sum) * 100) : 0;
            return (
              <button
                key={i}
                onClick={() => onSegment(filterKey, it.filterVal)}
                title={`${it.label}: ${it.value} (${pct}%)`}
                className={`w-full flex items-center gap-2 text-xs group transition-opacity ${isDim ? 'opacity-40' : ''} ${isActive ? 'font-semibold' : ''}`}
              >
                <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: it.hex }} />
                <span className="text-slate-300 group-hover:text-cyan-400 transition-colors truncate flex-1 text-left">{it.label}</span>
                <span className="text-slate-400 font-mono flex-shrink-0">{it.value}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const DetailCard = ({ label, children }) => (
  <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-3">
    <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">{label}</h4>
    <div className="text-sm">{children}</div>
  </div>
);

export default DomainsViewTable;